<script lang="ts" setup>
import { inject, computed, onMounted, reactive, ref } from 'vue';

import { i18n } from '@/i18n';

import Modal from '@/components/modal/Modal.vue';
import { DateService } from '@/coreDomainsModule/services/dateService';
import TextButton from '@/components/input/Btn/TextButton.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { simpleCopy } from '@/utils/deepCopy';
import { FORECAST_LENGTH, FORECAST_LENGTH_IN_MODAL } from '@/coreDomainsModule/constants';
import LeadTimePicker from '@/components/modal/LeadTimePicker.vue';
import StdInventoryPicker from '@/components/modal/StdInventoryPicker.vue';
import { FloatPopupState } from '@/appLogic/interfaces/floatPopup';
import { ISelectFormDataSet } from '@/coreDomainsModule/table';
import { objectEquals } from '@/utils/objectEquals';
import { key } from './useRecommendOrderStore';
import { StdIndexInfo, StdInventoryIndex } from '@/coreDomainsModule/entities/inventory';
import { IAnnounce } from '@/coreDomainsModule/entities/forecast';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';

import { BusinessService } from '../domains/services/businessService';
import NumberInputWithComma from '@/corePagesModule/components/NumberInputWithComma.vue';
import {
  EditReceiveAnnounceEvent,
  EditableReceiveAnnounceEventKeyNames,
  EditableReceiveAnnounceEventKeys,
} from '../domains/editReceiveAnnounceEvent';
import { calculateDataToDays, daysTocalculateDate } from '@/appLogic/business/businessLogic';
import { TableContentViewModel } from '@/viewModels/tableContent';
import { watch } from 'vue';
import SelectableNumberInput from '@/corePagesModule/components/SelectableNumberInput.vue';
import { SelectableNumberInputOptions } from '@/coreDomainsModule/entities/inputOptions';
import { getLeadTimeForSpanText } from '@/appLogic/business/inventorySimulation';

/**
 * 客先内示編集画面
 */
interface IRecommendEditReceiveAnnounce {
  calculatedAnnounceValues: IAnnounce[];
  leadTime: number;
  stdIndexInfos: StdInventoryIndex;
  leadTimePickerState: FloatPopupState; // リードタイムピッカーの表示状態
  leadTimeSet: ISelectFormDataSet;
  stdInventoryPickerState: FloatPopupState; // 標準在庫指数ピッカーの表示状態
  displayStdIndexInfoSet: StdIndexInfo;
  changedEventHistory: EditReceiveAnnounceEvent[];
  currentEventIndex: number;
  announceAdjustmentOptions: SelectableNumberInputOptions[];
}

const props = withDefaults(
  defineProps<{
    propCalculatedAnnounceValues: IAnnounce[];
    propLeadTime: number;
    propLeadTimeDisplayValue?: number;
    propLeadTimeUnit?: string;
    propStdIndexInfos: StdInventoryIndex;
  }>(),
  {
    propLeadTimeDisplayValue: 0,
    propLeadTimeUnit: 'day',
  }
);

const emits = defineEmits<{
  (e: 'confirmReceiveAnnounce'): void;
  (e: 'reviseReceiveAnnounce', announce: IAnnounce, leadTime: number, leadTimeSpanText: string, stdInfos: StdInventoryIndex): void;
}>();

//変数
const t = i18n.t;

const vm = new TableContentViewModel();

const store = inject(key);

const announceModalState = ref(false);

if (!store) {
  throw new Error('store is undefined');
}
BusinessService;
const state: IRecommendEditReceiveAnnounce = reactive<IRecommendEditReceiveAnnounce>({
  calculatedAnnounceValues: [],
  leadTime: 0,
  stdIndexInfos: {
    min: 0,
    max: 0,
    option: {
      unit: '',
    },
  },
  leadTimePickerState: { shouldShow: false, originCoordinate: { x: 0, y: 0 } },
  leadTimeSet: {
    displayData: -1,
    selectedUnit: 'day',
  },
  stdInventoryPickerState: { shouldShow: false, originCoordinate: { x: 0, y: 0 } },
  displayStdIndexInfoSet: { min: -1, max: 0, name: t('標準在庫指数(詳細ページ)'), unit: 'day' },
  changedEventHistory: [],
  currentEventIndex: -1,
  announceAdjustmentOptions: [],
}) as IRecommendEditReceiveAnnounce;

// イベントフック
onMounted(() => {
  state.calculatedAnnounceValues = simpleCopy(props.propCalculatedAnnounceValues);
  state.leadTime = props.propLeadTime;
  state.stdIndexInfos = simpleCopy(props.propStdIndexInfos);
  state.leadTimeSet.displayData = props.propLeadTimeDisplayValue;
  state.leadTimeSet.selectedUnit = props.propLeadTimeUnit;
  state.displayStdIndexInfoSet = {
    min: state.stdIndexInfos?.min,
    max: state.stdIndexInfos?.max,
    name: t('標準在庫指数(詳細ページ)'),
    unit: state.stdIndexInfos?.option?.unit,
  };
});

// watch

watch(
  () => props.propCalculatedAnnounceValues,
  (newValue) => {
    store.updateDefaultCustomerAnnounceIfNeeded(simpleCopy(newValue));
  }
);

watch(
  () => i18n.locale,
  () => {
    state.announceAdjustmentOptions = [];
    /**
     * 内示調整の倍率の選択肢 0.1 ~ 2.0
     */
    for (let i = 1; i <= 20; i++) {
      const value = i / 10;
      state.announceAdjustmentOptions.push({ text: `${value.toFixed(1)}${t('倍')}`, value });
    }
  },
  { immediate: true }
);

//computed

const canUndo = computed(() => {
  return state.changedEventHistory.length > 0 && state.currentEventIndex >= 0;
});

const canRedo = computed(() => {
  return state.changedEventHistory.length > 0 && state.currentEventIndex < state.changedEventHistory.length - 1;
});

// TODO: implement ↓

/**
 * 変更内容をスタックに保存する関数
 * @param key - 変更内容の項目
 * @param beforeValue - 変更前の値
 * @param afterValue - 変更後の値
 */
const pushEvent = (key: EditableReceiveAnnounceEventKeys, beforeValue: any, afterValue: any) => {
  // 現在のインデックス以降のイベントを削除
  state.changedEventHistory.splice(state.currentEventIndex + 1);
  // 履歴は最大件数まで保存し、それを超えた場合は古いものから削除する
  if (state.changedEventHistory.length >= 10) {
    // TODO: 10は仮置き
    state.changedEventHistory.shift();
  }
  // 変更内容をスタックに保存
  const event = { key, beforeValue, afterValue };
  state.changedEventHistory.push(event);
  // イベントを追加した場合、現在のインデックスは最新を指す
  state.currentEventIndex = state.changedEventHistory.length - 1;
};

/**
 * スタックから指定したインデックスの変更内容を取得する関数
 * @param index - 取得したいイベントのインデックス
 */
const getEvent = (index: number): EditReceiveAnnounceEvent | undefined => {
  return state.changedEventHistory[index];
};

/**
 * 現在の画面に変更内容を反映させる関数
 * @param key - 変更内容の項目
 * @param value - 変更内容
 */
const applyEventValue = (key: EditableReceiveAnnounceEventKeys, value: any) => {
  switch (key) {
    case EditableReceiveAnnounceEventKeyNames.forecast:
      state.calculatedAnnounceValues = value;
      break;
    case EditableReceiveAnnounceEventKeyNames.leadTime:
      state.leadTimeSet = value;
      break;
    case EditableReceiveAnnounceEventKeyNames.standardInventoryIndex:
      state.stdIndexInfos = value;
      break;
    default:
      break;
  }
};

/**
 * Undo(戻る)機能。変更内容を一つ前の更新履歴で上書きする関数
 */
const undo = () => {
  const event = getEvent(state.currentEventIndex);
  if (!event) {
    // indexが範囲外
    console.error('index out of range');
    return;
  }
  applyEventValue(event.key, simpleCopy(event.beforeValue));
  state.currentEventIndex--;
};

/**
 * Redo(進む)機能。変更内容を一つ後の更新履歴で上書きする関数
 */
const redo = () => {
  const event = getEvent(state.currentEventIndex + 1);
  if (!event) {
    // indexが範囲外
    console.error('index out of range');
    return;
  }
  applyEventValue(event.key, simpleCopy(event.afterValue));
  state.currentEventIndex++;
};

/**
 *
 * @param index - a
 * @param newValue - a
 */
const updateAmount = (index, newValue) => {
  if (newValue === state.calculatedAnnounceValues[index].amount) {
    // 変更がない場合は何もしない
    return;
  }
  const oldValue = simpleCopy(state.calculatedAnnounceValues);
  // calculatedAnnounceValuesの特定のインデックスを更新
  state.calculatedAnnounceValues[index].amount = newValue;
  // 変更内容をスタックに保存
  pushEvent(EditableReceiveAnnounceEventKeyNames.forecast, oldValue, simpleCopy(state.calculatedAnnounceValues));
};

/* picker */

/**
 * リードタイムピッカーを開く　FIXME: teleportとか使って、ピッカーを表示したい
 * @param event - ボタンのクリックイベント
 */
const openLeadTimePicker = (event: MouseEvent) => {
  // 2024-1-9 ひとまず、リードタイムピッカーを表示するボタンの位置にピッカーを表示するようにしている
  const leadTimeContent = document.getElementsByClassName('editable-infos-lead-time-container__lead-time')[0] as HTMLElement;
  state.leadTimePickerState.originCoordinate.x = leadTimeContent.offsetLeft;
  state.leadTimePickerState.originCoordinate.y = leadTimeContent.offsetTop + leadTimeContent.offsetHeight;
  state.leadTimePickerState.shouldShow = true;
};
/**
 * リードタイムピッカーを閉じる
 */
const closeLeadTimePicker = () => {
  state.leadTimePickerState.shouldShow = false;
};
/**
 * リードタイムピッカーで選択した値を反映させる
 * @param value - リードタイムピッカーで選択した値
 */
const onConfirmLeadTimePicker = (value: ISelectFormDataSet) => {
  if (
    (value.displayData === state.leadTimeSet.displayData && value.selectedUnit === state.leadTimeSet.selectedUnit) ||
    value.displayData == null
  ) {
    // 変更がない場合 or 空が入力されていた場合は何もしない
    closeLeadTimePicker();
    return;
  }

  const updatedLeadTimeSet: ISelectFormDataSet = {
    displayData: getLeadTimeForSpanText(value.displayData, value.selectedUnit.toString()),
    selectedUnit: value.selectedUnit,
  };

  // シミュレーションに使用するリードタイム値を更新（日数で管理）
  state.leadTime = calculateDataToDays(value);
  // 表示に使用するリードタイム値を更新
  if (updatedLeadTimeSet.displayData) store.changedLeadTimeDisplayValue(updatedLeadTimeSet.displayData);
  // 表示に使用するリードタイムの単位を更新
  if (typeof value.selectedUnit === 'string') store.changedLeadTimeUnit(updatedLeadTimeSet.selectedUnit.toString());
  const oldValue = simpleCopy(state.leadTimeSet);
  state.leadTimeSet = updatedLeadTimeSet;
  pushEvent(EditableReceiveAnnounceEventKeyNames.leadTime, oldValue, simpleCopy(state.leadTimeSet));
  closeLeadTimePicker();
};

/**
 * 標準在庫指数ピッカーを開く FIXME: teleportとか使って、ピッカーを表示したい
 * @param event - ボタンのクリックイベント
 */
const openStdInventoryPicker = (event: MouseEvent) => {
  // 2024-1-9 ひとまず、標準在庫指数ピッカーを表示するボタンの位置にピッカーを表示するようにしている
  const stdInventoryContent = document.getElementsByClassName('editable-infos-std-container__std-infos')[0] as HTMLElement;
  state.stdInventoryPickerState.originCoordinate.x = stdInventoryContent.offsetLeft;
  state.stdInventoryPickerState.originCoordinate.y = stdInventoryContent.offsetTop + stdInventoryContent.offsetHeight;
  state.stdInventoryPickerState.shouldShow = true;
};
/**
 * 標準在庫指数ピッカーを閉じる
 */
const closeStdInventoryPicker = () => {
  state.stdInventoryPickerState.shouldShow = false;
};
/**
 * 標準在庫指数ピッカーで選択した値を反映させる
 * @param value - 標準在庫指数ピッカーで選択した値
 */
const onStdInventoryPicker = (value: StdIndexInfo) => {
  if (value.min === state.stdIndexInfos.min && value.max === state.stdIndexInfos.max && value.unit === state.stdIndexInfos.option.unit) {
    // 変更がない場合は何もしない
    closeStdInventoryPicker();
    return;
  }
  state.displayStdIndexInfoSet = {
    min: value.min,
    max: value.max,
    name: t('標準在庫指数(詳細ページ)'),
    unit: value.unit,
  };
  const oldValue = simpleCopy(state.stdIndexInfos);
  state.stdIndexInfos = {
    min: value.min,
    max: value.max,
    option: {
      unit: value.unit,
    },
  };
  pushEvent(EditableReceiveAnnounceEventKeyNames.standardInventoryIndex, oldValue, simpleCopy(state.stdIndexInfos));
  closeStdInventoryPicker();
};

/**
 * 標準在庫指数ピッカーで選択した値が不正だった場合の処理
 */
const onConfirmedInvalidStdInventoryPicker = () => {
  // TODO: ひとまず変更を適用せずにピッカーを閉じている。アラート出すなどの通知が必要
  closeStdInventoryPicker();
};

/**
 *
 */
const selectLeadtime = () => {};
/**
 *
 */
const selectStdIndexed = () => {};

/**
 *
 */
const onClickConfirmReceiveAnnounce = () => {
  // propsに変化があった | リセットフラグが立った場合に、シミュレーションを更新する
  if (
    !objectEquals(state.calculatedAnnounceValues, props.propCalculatedAnnounceValues) ||
    state.leadTime !== props.propLeadTime ||
    !objectEquals(state.stdIndexInfos, props.propStdIndexInfos)
  ) {
    // 確定発注が存在する場合、最終確定日の翌日の日付を取得し、extraSimulateを更新する
    const lockedRecommend = store.state.recommend.filter((item) => item.isLocked || item.arrivalId != '') ?? [];
    const lockedRecommendLast = lockedRecommend?.[lockedRecommend?.length - 1];

    const startDate = store.calculateStartDate(lockedRecommendLast);

    if (startDate) {
      store.updateExtraSimulate(state.calculatedAnnounceValues, state.stdIndexInfos, state.leadTime, lockedRecommend, startDate);
    } else {
      store.updateExtraSimulate(state.calculatedAnnounceValues, state.stdIndexInfos, state.leadTime);
    }
  }
  emits('confirmReceiveAnnounce');
};

/**
 * 内示調整ボタンクリックの処理
 */
const onClickAnnounceAdjustment = () => {
  store.calcAnnounceAdjustment(state.calculatedAnnounceValues);
};

/**
 * 客先内示記入モーダルを表示する
 */
const onClickAnnounceModalOpenButton = () => {
  announceModalState.value = true;
};

/**
 * 客先内示記入モーダルを閉じる
 */
const onClickAnnounceModalCloseButton = () => {
  announceModalState.value = false;
};

/**
 * 客先内示記入モーダルを閉じる
 */
const closeAnnounceModal = () => {
  announceModalState.value = false;
};

/**
 * 内示調整の倍率が変更された時の処理
 * @param value - 変更された倍率
 */
const onConfirmedAnnounceAdjustmentRate = (value: number) => {
  store.updateAnnounceAdjustmentRate(value);
};
</script>

<template>
  <div class="recommend-edit-receive-announce rounded-base-middle">
    <!-- <CheckBox class="recommend-edit-receive-announce__check-box" :label="'客先内示と同じ'" /> -->
    <div class="recommend-edit-receive-announce__showing-controller showing-controller">
      <span class="showing-controller__title">
        {{ t('客先内示') }}
      </span>
      <div class="showing-controller__state-icon">
        <!-- TODO: 開閉切り替え実装する -->
        <SvgLoader
          class="showing-controller__state-icon--opened"
          :tool-tip-value="t('客先内示エリアを閉じる(詳細ページ)')"
          :name="'arrowDownLarge'"
          data-testid="`showing-controller__opened-arrow`"
          :button-style="'btn-primary'"
        />
      </div>
    </div>
    <div class="recommend-edit-receive-announce__announce-table-container announce-table-container">
      <div class="announce-table-container__editable-infos-controller-container table-infos-controller-container">
        <div class="table-infos-controller-container__sequence-buttons">
          <SvgLoader
            class="table-infos-controller-container__sequence-buttons--back"
            :name="'leftArrow'"
            data-testid="`offered_announce_undo_button`"
            :tool-tip-value="t('計算結果を前に戻す(詳細ページ)')"
            :button-style="'btn-primary'"
            :disabled="!canUndo"
            @click="undo"
          />
          <SvgLoader
            class="table-infos-controller-container__sequence-buttons--next"
            :name="'rightArrow'"
            :data-testid="`offered-announce_undo_button`"
            :tool-tip-value="t('計算結果を次に進める(詳細ページ)')"
            :disabled="!canRedo"
            @click="redo"
          />
        </div>
        <div class="table-infos-controller-container__show-all-announce">
          <!-- 客先内示記入モーダルを表示するボタン -->
          <span class="table-infos-controller-container__show-all-announce--text" @click="onClickAnnounceModalOpenButton">
            {{ t('全ての月を表示(詳細ページ)') }}
          </span>
          <SvgLoader
            class="table-infos-controller-container__show-all-announce--icon"
            :tool-tip-value="t('客先内示エリアを閉じる(詳細ページ)')"
            :name="'doubleArrow'"
            data-testid="`table-infos-controller-container__show-all-announce--icon`"
            :button-style="'btn-primary'"
          />
        </div>
      </div>
      <div class="announce-table-container__table-container table-container">
        <table class="table-container__announce-table-area announce-table-area">
          <thead class="announce-table-area__table-head--container">
            <tr class="announce-table-area__table-head--row">
              <!-- 1列目 -->
              <th class="announce-table-area__table-head--column">
                {{ t('') }}
              </th>
              <!-- 2列目以降 -->
              <th
                v-for="(testObj, index) in state.calculatedAnnounceValues.slice(0, FORECAST_LENGTH)"
                :key="'header' + index"
                class="announce-table-area__table-head--column"
              >
                {{
                  t('displayShortDate', {
                    year: t(`${testObj.year}`),
                    month: t(`${testObj.month}`),
                  })
                }}
              </th>
            </tr>
          </thead>
          <tbody class="announce-table-area__table-body">
            <tr class="announce-table-area__table-body--row">
              <!-- シミュレーション行 -->
              <td class="announce-table-area__table-body--column">
                {{ t('シミュレーション(詳細ページ)') }}
              </td>
              <template v-for="(testObj, index) in state.calculatedAnnounceValues.slice(0, FORECAST_LENGTH)" :key="'contents' + index">
                <td class="announce-table-area__table-body--column">
                  <NumberInputWithComma
                    :model-value="testObj.amount"
                    class="announce-table-area__table-body--input-column"
                    @update:model-value="updateAmount(index, $event)"
                  />
                </td>
              </template>
            </tr>
            <tr class="announce-table-area__table-body--row">
              <!-- 受領内示行 -->
              <td class="announce-table-area__table-body--column">
                {{ t('受領した内示(詳細ページ)') }}
              </td>
              <template v-for="(testObj, index) in store.state.defaultCustomerAnnounce.slice(0, FORECAST_LENGTH)" :key="'default' + index">
                <td class="announce-table-area__table-body--column">
                  <div class="table-container__announce-default-item">
                    <span class="table-container__announce-default-item--value">{{ testObj.amount.addComma() }}</span>
                  </div>
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <!-- 内示調整、リードタイム、標準在庫指数といった数値の変更を行うUIを設置する -->
    <div class="recommend-edit-receive-announce__editable-infos-container editable-infos-container">
      <!-- 内示調整の倍率を選択する -->
      <div class="editable-infos-container__editable-infos-announce-adjustment-container editable-infos-announce-adjustment-container">
        <i18n-t class="editable-infos-announce-adjustment-container__title" :keypath="'内示調整(詳細ページ)'" tag="span">
          <template #newLine>
            <br />
          </template>
        </i18n-t>
        <SelectableNumberInput
          :display-value="`${store.state.announceAdjustmentRate} ${t('倍')}`"
          :default-value="store.state.announceAdjustmentRate"
          :options="state.announceAdjustmentOptions"
          class="editable-infos-announce-adjustment-container__leverage-selector"
          @confirmed="onConfirmedAnnounceAdjustmentRate"
        />
      </div>
      <div class="recommend-edit-receive-announce__editable-infos-lead-time-container editable-infos-lead-time-container">
        <span class="editable-infos-lead-time-container__title">
          {{ t('リードタイム') }}
        </span>
        <div class="editable-infos-lead-time-container__lead-time" @click="openLeadTimePicker">
          <div class="editable-infos-lead-time-container__lead-time--value">{{ vm.getDisplayLeadTimeWithUnit(state.leadTimeSet) }}</div>
          <SvgLoader
            class="editable-infos-lead-time-container__lead-time--selector"
            :name="'arrowDown'"
            :data-testid="`offered-announce_undo_button`"
            @click="selectLeadtime"
          />
        </div>
      </div>
      <div class="recommend-edit-receive-announce__editable-infos-std-container editable-infos-std-container">
        <span class="editable-infos-std-container__title">
          {{ t('標準在庫指数') }}
        </span>
        <div class="editable-infos-std-container__std-infos" @click="openStdInventoryPicker">
          <div class="editable-infos-std-container__std-infos--value">
            {{ vm.getDisplayStdInventory({} as IBusiness, state.stdIndexInfos) }}
          </div>
          <SvgLoader
            class="editable-infos-std-container__std-infos--selector"
            :name="'arrowDown'"
            :data-testid="`offered-announce_undo_button`"
            @click="selectStdIndexed"
          />
        </div>
      </div>
    </div>
    <LeadTimePicker
      class="lead-time-picker"
      :popup-state="state.leadTimePickerState.shouldShow"
      :lead-time-set="state.leadTimeSet"
      @on-close="closeLeadTimePicker"
      @on-confirm="onConfirmLeadTimePicker"
      @on-update-lead-time="(value, unit) => store.onUpdateDefaultLeadTime(value, unit)"
    />
    <StdInventoryPicker
      class="std-inventory-picker"
      :popup-state="state.stdInventoryPickerState.shouldShow"
      :std-inventory-info="state.displayStdIndexInfoSet"
      @on-close="closeStdInventoryPicker"
      @on-confirm="onStdInventoryPicker"
      @on-confirmed-invalid-value="onConfirmedInvalidStdInventoryPicker"
      @on-update-std-inventory="(value) => store.onUpdateDefaultStdInventoryIndices(value)"
    />
    <!-- 「変更クリア」、「決定」ボタン -->
    <div v-if="store.state.progress === 'EditReceiveAnnounce'" class="recommend-edit-buttons">
      <TextButton
        class="editable-infos__adjust-announce-button"
        :button-style="'btn-cancel'"
        :size="'btn-md'"
        :on-click="onClickAnnounceAdjustment"
      >
        <i18n-t :keypath="'内示調整倍率を適用(詳細ページ)'" tag="p">
          <template #newLine>
            <br />
          </template>
        </i18n-t>
      </TextButton>
      <TextButton
        class="recommend-edit-receive-announce__define-button"
        :button-style="'btn-primary'"
        :size="'btn-md'"
        :on-click="onClickConfirmReceiveAnnounce"
      >
        {{ t('発注計画を作成(詳細ページ)') }}
      </TextButton>
    </div>
    <!-- 客先内示入力モーダル（26ヶ月分） -->
    <Modal
      :stretch-container="true"
      modal-title="直近全ての月の客先内示(詳細ページ)"
      modal-size="3xl"
      border-radius="16px"
      :modal-state="announceModalState"
      @outside-click="closeAnnounceModal"
    >
      <div class="announce-modal__content">
        <template v-for="(testObj, index) in state.calculatedAnnounceValues" :key="'modal-header' + index">
          <div class="announce-modal__content-header">
            {{
              t('displayShortDate', {
                year: t(`${testObj.year}`),
                month: t(`${testObj.month}`),
              })
            }}
          </div>
        </template>
        <template v-for="(testObj, index) in state.calculatedAnnounceValues" :key="'modal-contents' + index">
          <NumberInputWithComma
            :model-value="testObj.amount"
            class="announce-modal__content-item"
            @update:model-value="updateAmount(index, $event)"
          />
        </template>
        <!-- デフォルト値 -->
        <template v-for="(testObj, index) in store.state.defaultCustomerAnnounce" :key="'modal-defaults' + index">
          <div class="announce-modal__content-default">
            <span class="upper-asterisk-mark">※</span>
            <span class="announce-modal__content-default--value"> {{ testObj.amount.addComma() }} </span>
          </div>
        </template>
      </div>
      <div class="announce-modal-close-button-container">
        <TextButton
          class="announce-modal__close-button"
          :button-style="'btn-cancel'"
          :size="'btn-md'"
          :on-click="onClickAnnounceModalCloseButton"
        >
          {{ t('閉じる') }}
        </TextButton>
      </div>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.recommend-edit-receive-announce {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto auto minmax(0, 1fr);
  row-gap: 16px;
  padding: 6px 14px 22px 14px;
  background-color: $semanticWhite !important;
  border-radius: 4px;
  border: 2px solid $semanticPrimary;
  box-shadow: 0px 0px 16px 0px rgba(30, 146, 239, 0.5);

  &__showing-controller {
    padding: 0px 2.5px 0px 2.5px;
  }
}

.showing-controller {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 8px;

  &__title {
    font-family: Inter;
    font-size: $fontSizeM;
    color: $textDefault;
    line-height: 23px;
    font-weight: 700;
    justify-self: start;
  }

  &__state-icon {
    width: 28px;
    height: 28px;
    padding-top: 9.33px; //top指定できないので、代わり
    padding-left: 7px; //left指定できないので、代わり
    justify-self: end;
  }
}

.announce-table-container {
  display: grid;
  grid-template-rows: auto 1fr;
  row-gap: 4px;

  .table-infos-controller-container {
    display: grid;
    grid-template-columns: auto 1fr;

    &__sequence-buttons {
      display: grid;
      grid-template-columns: auto 1fr;
      column-gap: 16px;
      justify-self: start;
      &--back {
        width: 18px;
        height: 18px;
        padding-top: 5.25px; //top指定できないので、代わり
        padding-left: 1.5px; //left指定できないので、代わり
      }
      &--next {
        width: 18px;
        height: 18px;
        padding-top: 5.25px; //top指定できないので、代わり
        padding-left: 1.5px; //left指定できないので、代わり
      }
    }

    &__show-all-announce {
      display: grid;
      grid-template-columns: auto 1fr;
      column-gap: 4px;
      justify-self: end;
      &--text {
        font-family: Inter;
        font-size: $fontSizeS;
        font-weight: 400;
        line-height: 18px;
        color: $semanticPrimary;
      }
      &--icon {
        width: 18px;
        height: 18px;
        padding-top: 2.75px; //top指定できないので、代わり
        padding-left: 3px; //left指定できないので、代わり
      }
      &:hover {
        cursor: pointer;
      }
    }
  }

  .table-container {
    width: 100%;
    height: 98px;
    overflow: hidden;
    border-radius: 4px;
    border: solid 1px $borderDefault;

    &__table-area {
      width: auto;
      height: auto;
    }
  }
}

.announce-table-area {
  width: 100%;
  &__table-head {
    &--row {
      height: 30px;
      text-align: right;
    }
    &--column {
      min-width: 106.6px;
      &:last-child {
        padding-right: 8px;
      }
    }
    &--container {
      background-color: $surfaceDefault;
    }
  }
  &__table-body {
    background-color: $semanticWhite;
    &--row {
      height: 34px;
      text-align: right;
    }
    &--column {
      min-width: 106.6px;
      &:last-child {
        padding-right: 8px;
      }
    }
  }
}

.announce-table-area__table-body--input-column {
  width: 98.6px;
  text-align: right;
  background-color: transparent;
}

.editable-infos-container {
  display: grid;
  grid-template-columns: auto repeat(2, minmax(0, 1fr));
  column-gap: 12px;
  align-items: center;

  .editable-infos-announce-adjustment-container {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    column-gap: 8px;
    &__title {
      font-family: Inter;
      font-size: $fontSizeS;
      font-weight: 400;
      line-height: 20px;
      text-align: center;
      margin: auto 0px;
    }
    &__leverage-selector {
      padding: 4px 8px 3px 8px;
      font-family: Inter;
      font-size: $fontSizeS;
      font-weight: 400;
      line-height: 20px;
      border-bottom: 1px solid $borderDefault;
    }
  }

  .editable-infos-lead-time-container {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    column-gap: 8px;
    &__title {
      font-family: Inter;
      font-size: $fontSizeS;
      font-weight: 400;
      line-height: 20px;
      text-align: center;
      margin: auto 0px;
    }
    &__lead-time {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 16px);
      align-items: center;
      &:hover {
        cursor: pointer;
      }
      padding: 4px 8px 3px 8px;
      border-bottom: 1px solid $borderDefault;
      &--value {
        font-family: Inter;
        font-size: $fontSizeS;
        font-weight: 400;
        line-height: 20px;
        justify-items: start;
      }
      &--selector {
        justify-self: end;
      }
    }
  }

  .editable-infos-std-container {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    column-gap: 8px;
    &__title {
      font-family: Inter;
      font-size: $fontSizeS;
      font-weight: 400;
      line-height: 20px;
      text-align: center;
      margin: auto 0px;
    }
    &__std-infos {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 16px);
      align-items: center;
      &:hover {
        cursor: pointer;
      }
      padding: 4px 8px 3px 8px;
      border-bottom: 1px solid $borderDefault;
      &--value {
        font-family: Inter;
        font-size: $fontSizeS;
        font-weight: 400;
        line-height: 20px;
        justify-items: start;
      }
      &--selector {
        justify-self: end;
      }
    }
  }
}

.recommend-edit-buttons {
  display: grid;
  grid-template-columns: repeat(2, auto);
  column-gap: 8px;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
}

.lead-time-picker {
  position: absolute;
  // クリックしたボタンの位置にピッカーを表示する
  top: v-bind('`${state.leadTimePickerState.originCoordinate.y}px`');
  left: v-bind('`${state.leadTimePickerState.originCoordinate.x}px`');
}
.std-inventory-picker {
  position: absolute;
  // クリックしたボタンの位置にピッカーを表示する
  top: v-bind('`${state.stdInventoryPickerState.originCoordinate.y}px`');
  left: v-bind('`${state.stdInventoryPickerState.originCoordinate.x}px`');
}

.announce-modal {
  &__content {
    display: grid;
    align-items: center;
    justify-items: end;
    grid-template-columns: v-bind('`repeat(${FORECAST_LENGTH_IN_MODAL}, minmax(100px, 1fr))`');
    grid-template-rows: repeat(3, minmax(0, 1fr));
    overflow-x: auto;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    height: 98px;

    &-header {
      font-size: $fontSizeS;
      font-weight: bold;
    }

    &-item {
      font-size: $fontSizeS;
      width: 100%;
      background-color: initial;
      text-align: right;
      color: $textSecondary;
    }

    &-default {
      font-size: $fontSizeS;
      color: $textSecondary;
    }

    &__close-button {
      width: 144px;
    }
  }
}

.announce-modal-close-button-container {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

.upper-asterisk-mark {
  font-size: calc(#{$fontSizeS} * 0.625); // Figmaのサイズに合わせて指定
  vertical-align: top;
}

.editable-infos__adjust-announce-button {
  padding: 12px 8px;
}
</style>
